import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Grid, Card, CardContent, Button, LinearProgress,
  Chip, Alert, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, List, ListItem, ListItemText, ListItemSecondaryAction,
  IconButton, Checkbox, Select, MenuItem, FormControl, InputLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tabs, Tab, Divider, Tooltip, Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import {
  Add, Delete, ExpandMore, CheckCircle, Cancel, ExitToApp,
  PersonOff, AccessTime, Assignment,
} from '@mui/icons-material';
import { apiService } from '../../services/api';
import { displayName } from '../../types';

const REASONS = [
  { value: 'resignation', label: 'Resignation' },
  { value: 'termination', label: 'Termination' },
  { value: 'retirement', label: 'Retirement' },
  { value: 'contract_end', label: 'Contract End' },
  { value: 'redundancy', label: 'Redundancy' },
  { value: 'other', label: 'Other' },
];

const CATEGORIES = ['IT', 'HR', 'Finance', 'Admin', 'Legal', 'Other'];

const statusColor: Record<string, any> = {
  in_progress: 'warning',
  completed: 'success',
  cancelled: 'error',
};

const taskStatusColor: Record<string, any> = {
  pending: 'default',
  completed: 'success',
  skipped: 'info',
};

const Offboarding: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [offboardings, setOffboardings] = useState<any[]>([]);
  const [templates, setTemplates] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Detail dialog
  const [detailRecord, setDetailRecord] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // New offboarding dialog
  const [newOpen, setNewOpen] = useState(false);
  const [newEmployee, setNewEmployee] = useState('');
  const [newTemplate, setNewTemplate] = useState('');
  const [newLastDay, setNewLastDay] = useState('');
  const [newReason, setNewReason] = useState('resignation');
  const [newReasonNotes, setNewReasonNotes] = useState('');
  const [saving, setSaving] = useState(false);

  // New template dialog
  const [templateOpen, setTemplateOpen] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDesc, setTemplateDesc] = useState('');
  const [templateTasks, setTemplateTasks] = useState([
    { title: '', category: 'IT', isRequired: false, description: '' },
  ]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [obs, tmpl, emps] = await Promise.all([
        apiService.getAllOffboardings().catch(() => []),
        apiService.getOffboardingTemplates().catch(() => []),
        apiService.getEmployees().catch(() => []),
      ]);
      setOffboardings(obs);
      setTemplates(tmpl);
      setEmployees(emps);
    } catch {
      setError('Failed to load offboarding data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const openDetail = async (id: string) => {
    setDetailLoading(true);
    setDetailRecord(null);
    try {
      const data = await apiService.getOffboarding(id);
      setDetailRecord(data);
    } catch {
      setError('Failed to load offboarding details');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newEmployee || !newLastDay) return;
    setSaving(true);
    try {
      await apiService.createOffboarding({
        employeeId: newEmployee,
        templateId: newTemplate || undefined,
        lastWorkingDay: newLastDay,
        reason: newReason,
        reasonNotes: newReasonNotes || undefined,
      });
      setSuccess('Offboarding initiated successfully');
      setNewOpen(false);
      setNewEmployee(''); setNewTemplate(''); setNewLastDay('');
      setNewReason('resignation'); setNewReasonNotes('');
      fetchData();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to create offboarding');
    } finally {
      setSaving(false);
    }
  };

  const handleTaskToggle = async (offboardingId: string, taskId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'completed' ? 'pending' : 'completed';
    try {
      await apiService.updateOffboardingTask(offboardingId, taskId, { status: newStatus });
      const updated = await apiService.getOffboarding(offboardingId);
      setDetailRecord(updated);
      // Refresh list too
      setOffboardings(prev => prev.map(o => o.id === offboardingId ? { ...o, tasks: updated.tasks } : o));
    } catch {
      setError('Failed to update task');
    }
  };

  const handleComplete = async (id: string) => {
    try {
      await apiService.completeOffboarding(id);
      setSuccess('Offboarding marked as completed');
      setDetailRecord(null);
      fetchData();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to complete offboarding');
    }
  };

  const handleCancel = async (id: string) => {
    if (!window.confirm('Cancel this offboarding?')) return;
    try {
      await apiService.cancelOffboarding(id);
      setSuccess('Offboarding cancelled');
      setDetailRecord(null);
      fetchData();
    } catch {
      setError('Failed to cancel offboarding');
    }
  };

  const handleCreateTemplate = async () => {
    if (!templateName.trim()) return;
    setSaving(true);
    try {
      await apiService.createOffboardingTemplate({
        name: templateName,
        description: templateDesc || undefined,
        tasks: templateTasks.filter(t => t.title.trim()).map((t, i) => ({
          title: t.title, category: t.category,
          description: t.description || undefined,
          isRequired: t.isRequired, order: i,
        })),
      });
      setSuccess('Template created');
      setTemplateOpen(false);
      setTemplateName(''); setTemplateDesc('');
      setTemplateTasks([{ title: '', category: 'IT', isRequired: false, description: '' }]);
      fetchData();
    } catch {
      setError('Failed to create template');
    } finally {
      setSaving(false);
    }
  };

  const getProgress = (record: any) => {
    const tasks = record.tasks || [];
    if (!tasks.length) return 0;
    const done = tasks.filter((t: any) => t.status === 'completed' || t.status === 'skipped').length;
    return Math.round((done / tasks.length) * 100);
  };

  const inProgress = offboardings.filter(o => o.status === 'in_progress');
  const completed = offboardings.filter(o => o.status === 'completed');
  const cancelled = offboardings.filter(o => o.status === 'cancelled');

  if (loading) return (
    <Box display="flex" justifyContent="center" alignItems="center" height="400px">
      <CircularProgress />
    </Box>
  );

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>Offboarding</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage employee exits with structured checklists
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button variant="outlined" startIcon={<Assignment />} onClick={() => setTemplateOpen(true)}>
            New Template
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => setNewOpen(true)}
            sx={{ bgcolor: '#C08A2E', '&:hover': { bgcolor: '#A3501A' } }}>
            Initiate Offboarding
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary">In Progress</Typography>
                  <Typography variant="h4" color="warning.main">{inProgress.length}</Typography>
                </Box>
                <AccessTime sx={{ fontSize: 40, color: 'warning.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary">Completed</Typography>
                  <Typography variant="h4" color="success.main">{completed.length}</Typography>
                </Box>
                <CheckCircle sx={{ fontSize: 40, color: 'success.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary">Templates</Typography>
                  <Typography variant="h4" color="primary">{templates.length}</Typography>
                </Box>
                <Assignment sx={{ fontSize: 40, color: 'primary.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label={`In Progress (${inProgress.length})`} />
          <Tab label={`Completed (${completed.length})`} />
          <Tab label={`Cancelled (${cancelled.length})`} />
          <Tab label={`Templates (${templates.length})`} />
        </Tabs>
      </Paper>

      {/* In Progress / Completed / Cancelled Tables */}
      {tab <= 2 && (() => {
        const rows = [inProgress, completed, cancelled][tab];
        return (
          <Paper>
            {rows.length === 0 ? (
              <Box p={4} textAlign="center">
                <PersonOff sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                <Typography color="text.secondary">No records found</Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Employee</TableCell>
                      <TableCell>Department</TableCell>
                      <TableCell>Last Working Day</TableCell>
                      <TableCell>Reason</TableCell>
                      <TableCell>Progress</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map(record => {
                      const progress = getProgress(record);
                      return (
                        <TableRow key={record.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {record.employee ? displayName(record.employee) : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>{record.employee?.department?.name || '—'}</TableCell>
                          <TableCell>
                            {record.lastWorkingDay
                              ? new Date(record.lastWorkingDay).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                              : '—'}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={REASONS.find(r => r.value === record.reason)?.label || record.reason}
                              size="small" variant="outlined"
                            />
                          </TableCell>
                          <TableCell sx={{ minWidth: 140 }}>
                            {record.tasks?.length > 0 ? (
                              <Box>
                                <Box display="flex" justifyContent="space-between" mb={0.5}>
                                  <Typography variant="caption">{progress}%</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {record.tasks.filter((t: any) => t.status === 'completed').length}/{record.tasks.length}
                                  </Typography>
                                </Box>
                                <LinearProgress variant="determinate" value={progress} color={progress === 100 ? 'success' : 'warning'} />
                              </Box>
                            ) : (
                              <Typography variant="caption" color="text.secondary">No tasks</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip label={record.status.replace('_', ' ')} color={statusColor[record.status]} size="small" />
                          </TableCell>
                          <TableCell align="right">
                            <Button size="small" variant="outlined" onClick={() => openDetail(record.id)}>
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        );
      })()}

      {/* Templates Tab */}
      {tab === 3 && (
        <Box>
          {templates.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Assignment sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography color="text.secondary" gutterBottom>No templates yet</Typography>
              <Button variant="contained" onClick={() => setTemplateOpen(true)} sx={{ mt: 1 }}>
                Create First Template
              </Button>
            </Paper>
          ) : (
            <Grid container spacing={2}>
              {templates.map(tmpl => (
                <Grid key={tmpl.id} size={{ xs: 12, md: 6 }}>
                  <Card>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Box>
                          <Typography variant="h6">{tmpl.name}</Typography>
                          {tmpl.description && (
                            <Typography variant="body2" color="text.secondary">{tmpl.description}</Typography>
                          )}
                        </Box>
                        <Chip label={tmpl.isActive ? 'Active' : 'Inactive'} color={tmpl.isActive ? 'success' : 'default'} size="small" />
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        {tmpl.tasks?.length || 0} tasks
                      </Typography>
                      {tmpl.tasks?.length > 0 && (
                        <List dense sx={{ mt: 1 }}>
                          {[...tmpl.tasks].sort((a: any, b: any) => a.order - b.order).slice(0, 5).map((task: any) => (
                            <ListItem key={task.id} disablePadding>
                              <ListItemText
                                primary={task.title}
                                secondary={task.category}
                                primaryTypographyProps={{ variant: 'body2' }}
                                secondaryTypographyProps={{ variant: 'caption' }}
                              />
                              {task.isRequired && <Chip label="Required" size="small" color="error" variant="outlined" sx={{ ml: 1 }} />}
                            </ListItem>
                          ))}
                          {tmpl.tasks.length > 5 && (
                            <ListItem disablePadding>
                              <ListItemText
                                primary={`+${tmpl.tasks.length - 5} more tasks`}
                                primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                              />
                            </ListItem>
                          )}
                        </List>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}

      {/* Detail Dialog */}
      <Dialog open={!!detailRecord || detailLoading} onClose={() => setDetailRecord(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {detailRecord ? `Offboarding — ${displayName(detailRecord.employee)}` : 'Loading...'}
            </Typography>
            {detailRecord && (
              <Chip label={detailRecord.status.replace('_', ' ')} color={statusColor[detailRecord.status]} size="small" />
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {detailLoading && <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>}
          {detailRecord && (
            <Box>
              {/* Summary */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={6}>
                  <Typography variant="caption" color="text.secondary">Department</Typography>
                  <Typography>{detailRecord.employee?.department?.name || '—'}</Typography>
                </Grid>
                <Grid size={6}>
                  <Typography variant="caption" color="text.secondary">Position</Typography>
                  <Typography>{detailRecord.employee?.position?.name || '—'}</Typography>
                </Grid>
                <Grid size={6}>
                  <Typography variant="caption" color="text.secondary">Last Working Day</Typography>
                  <Typography>
                    {new Date(detailRecord.lastWorkingDay).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </Typography>
                </Grid>
                <Grid size={6}>
                  <Typography variant="caption" color="text.secondary">Reason</Typography>
                  <Typography>{REASONS.find(r => r.value === detailRecord.reason)?.label || detailRecord.reason}</Typography>
                </Grid>
                {detailRecord.reasonNotes && (
                  <Grid size={12}>
                    <Typography variant="caption" color="text.secondary">Notes</Typography>
                    <Typography variant="body2">{detailRecord.reasonNotes}</Typography>
                  </Grid>
                )}
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Task checklist */}
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Checklist ({detailRecord.tasks?.filter((t: any) => t.status === 'completed').length || 0}/{detailRecord.tasks?.length || 0} done)
                </Typography>
                <Typography variant="body2" color="text.secondary">{getProgress(detailRecord)}% complete</Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={getProgress(detailRecord)}
                color={getProgress(detailRecord) === 100 ? 'success' : 'warning'}
                sx={{ mb: 2, height: 8, borderRadius: 4 }}
              />

              {detailRecord.tasks?.length === 0 && (
                <Typography variant="body2" color="text.secondary">No tasks in this offboarding checklist.</Typography>
              )}

              {/* Group tasks by category */}
              {Object.entries(
                (detailRecord.tasks || []).reduce((acc: any, task: any) => {
                  const cat = task.category || 'General';
                  if (!acc[cat]) acc[cat] = [];
                  acc[cat].push(task);
                  return acc;
                }, {})
              ).map(([category, tasks]: any) => (
                <Accordion key={category} defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography fontWeight={500}>{category}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      ({tasks.filter((t: any) => t.status === 'completed').length}/{tasks.length})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0 }}>
                    <List dense>
                      {tasks.sort((a: any, b: any) => a.order - b.order).map((task: any) => (
                        <ListItem
                          key={task.id}
                          onClick={() => detailRecord.status === 'in_progress' && handleTaskToggle(detailRecord.id, task.id, task.status)}
                          sx={{ cursor: detailRecord.status === 'in_progress' ? 'pointer' : 'default', '&:hover': detailRecord.status === 'in_progress' ? { bgcolor: 'action.hover' } : {} }}
                        >
                          <Checkbox
                            checked={task.status === 'completed'}
                            disabled={detailRecord.status !== 'in_progress'}
                            size="small"
                            sx={{ mr: 1 }}
                          />
                          <ListItemText
                            primary={task.title}
                            secondary={task.description}
                            primaryTypographyProps={{
                              variant: 'body2',
                              sx: { textDecoration: task.status === 'completed' ? 'line-through' : 'none', color: task.status === 'completed' ? 'text.secondary' : 'text.primary' },
                            }}
                          />
                          <Chip label={task.status} color={taskStatusColor[task.status]} size="small" />
                        </ListItem>
                      ))}
                    </List>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailRecord(null)}>Close</Button>
          {detailRecord?.status === 'in_progress' && (
            <>
              <Button color="error" onClick={() => handleCancel(detailRecord.id)}>
                Cancel Offboarding
              </Button>
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircle />}
                onClick={() => handleComplete(detailRecord.id)}
                disabled={getProgress(detailRecord) < 100}
              >
                {getProgress(detailRecord) < 100 ? `Complete (${getProgress(detailRecord)}%)` : 'Mark Complete'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* New Offboarding Dialog */}
      <Dialog open={newOpen} onClose={() => setNewOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Initiate Offboarding</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2, mb: 2 }} required>
            <InputLabel>Employee</InputLabel>
            <Select value={newEmployee} onChange={e => setNewEmployee(e.target.value)} label="Employee">
              {employees.map((emp: any) => (
                <MenuItem key={emp.id} value={emp.id}>{displayName(emp)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth label="Last Working Day" type="date" value={newLastDay}
            onChange={e => setNewLastDay(e.target.value)}
            InputLabelProps={{ shrink: true }} required sx={{ mb: 2 }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Reason</InputLabel>
            <Select value={newReason} onChange={e => setNewReason(e.target.value)} label="Reason">
              {REASONS.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField
            fullWidth label="Additional Notes" multiline rows={2} value={newReasonNotes}
            onChange={e => setNewReasonNotes(e.target.value)} sx={{ mb: 2 }}
          />
          <FormControl fullWidth>
            <InputLabel>Checklist Template (optional)</InputLabel>
            <Select value={newTemplate} onChange={e => setNewTemplate(e.target.value)} label="Checklist Template (optional)">
              <MenuItem value=""><em>No template</em></MenuItem>
              {templates.map((t: any) => <MenuItem key={t.id} value={t.id}>{t.name} ({t.tasks?.length || 0} tasks)</MenuItem>)}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={saving || !newEmployee || !newLastDay}
            sx={{ bgcolor: '#C08A2E', '&:hover': { bgcolor: '#A3501A' } }}>
            {saving ? <CircularProgress size={20} /> : 'Initiate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* New Template Dialog */}
      <Dialog open={templateOpen} onClose={() => setTemplateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Offboarding Template</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Template Name" value={templateName} onChange={e => setTemplateName(e.target.value)}
            required sx={{ mt: 2, mb: 2 }} />
          <TextField fullWidth label="Description" value={templateDesc} onChange={e => setTemplateDesc(e.target.value)}
            multiline rows={2} sx={{ mb: 2 }} />
          <Typography variant="subtitle2" gutterBottom>Checklist Tasks</Typography>
          {templateTasks.map((task, idx) => (
            <Box key={idx} display="flex" gap={1} mb={1} alignItems="flex-start">
              <TextField
                label="Task title" value={task.title} size="small" sx={{ flex: 2 }}
                onChange={e => {
                  const updated = [...templateTasks];
                  updated[idx] = { ...updated[idx], title: e.target.value };
                  setTemplateTasks(updated);
                }}
              />
              <FormControl size="small" sx={{ minWidth: 90 }}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={task.category} label="Category"
                  onChange={e => {
                    const updated = [...templateTasks];
                    updated[idx] = { ...updated[idx], category: e.target.value };
                    setTemplateTasks(updated);
                  }}
                >
                  {CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </Select>
              </FormControl>
              <IconButton size="small" color="error" onClick={() => setTemplateTasks(templateTasks.filter((_, i) => i !== idx))}
                disabled={templateTasks.length === 1}>
                <Delete fontSize="small" />
              </IconButton>
            </Box>
          ))}
          <Button size="small" startIcon={<Add />} onClick={() => setTemplateTasks([...templateTasks, { title: '', category: 'IT', isRequired: false, description: '' }])}>
            Add Task
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateTemplate} disabled={saving || !templateName.trim()}>
            {saving ? <CircularProgress size={20} /> : 'Create Template'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Offboarding;
